
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Container, Modal } from "react-bootstrap";
import { useAuth } from "../../../../contexts/AuthContext";
import { ReactFlow, Background, Controls, Position, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver'
import { CastType, IntegrationStatus, IQuery, IQuerySelectData } from "../../../../component/solution/Interfaces";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { useNavigate } from "react-router-dom";
import { FileCheck, Send, PlaneLanding, Pencil, RotateCcw, BookOpenText, Signature, FilePenLine, X } from "lucide-react";
import { QueryListProvider } from "../../../../component/solution/QueryTableList/QueryListContext";
import { QueryTableList } from "../../../../component/solution/QueryTableList/QueryTableList";


const currentStep: object = { 
    width: 80, 
    height: 40, 
    border: '1px dashed red', // Add a bold red border
    backgroundColor: '#f0f0f0', // Optional: background color
};
const normalStep: object = { 
    width: 80, 
    height: 40, 
};

const preAuthStep: object = {
    width: 80, 
    height: 40,
    display: 'block',
}

const preAuthStepInit: object = { 
    width: 80, 
    height: 40, 
    border: '1px dashed green', // Add a bold green border
    backgroundColor: '#e0f8e0', // Optional: background color  #f0f0f0
};

// WorkFlow - START  
const initNodes = [
    {
        id: 'FETCHED', // required
        position: { x: 30, y: 80 }, // required
        data: { label: 'Fetch' }, // required
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SEND', // required
        position: { x: 140, y: 80 }, // required
        data: { label: 'Send' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SENT', // required
        position: { x: 250, y: 80 }, // required
        data: { label: 'Sent' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SUCCESS', // required
        position: { x: 360, y: 80 }, // required
        data: { label: 'Success' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'ARRIVED', // required
        position: { x: 470, y: 80 }, // required
        data: { label: 'Arrived' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'CLOSED', // required
        position: { x: 580, y: 80 }, // required
        data: { label: 'Closed' }, // required
        targetPosition: Position.Left,
        style: normalStep
    }, 
    {
        id: 'ERROR', // required
        position: { x: 30, y: 10 }, // required
        data: { label: 'Error' }, // required
        targetPosition: Position.Right,
        style: normalStep
    },  
    {
        id: 'PREAUTH', // required
        position: { x: 415, y: 10 }, // required
        data: { label: 'Pre-Auth' }, // required
        style: normalStep,
    }                                                     
  ];

  const stepEdges = [
    { id: 'FETCHED-SEND', source: 'FETCHED', target: 'SEND', label: ">", type: 'step' },
    { id: 'SEND-SENT', source: 'SEND', target: 'SENT', label: ">", type: 'step' },
    { id: 'SENT-ERROR', source: 'SENT', target: 'ERROR', label: "<", type: 'step' },
    { id: 'SENT-SUCCESS', source: 'SENT', target: 'SUCCESS', label: ">", type: 'step' },
    { id: 'SUCCESS-ARRIVED', source: 'SUCCESS', target: 'ARRIVED', label: ">", type: 'step' },
    { id: 'ARRIVED-CLOSED', source: 'ARRIVED', target: 'CLOSED', label: ">", type: 'step' },
  ];

enum ShipmentStatus {
    TO_FETCH = "TO_FETCH", 
    FETCHED = "FETCHED",
    TO_SEND = "TO_SEND",
    SENT = "SENT",
    SUCCESS = "SUCCESS", // M24L Validation Success
    ERROR = "ERROR", // M24L Validation Failed
    ARRIVED = "ARRIVED",
    CLOSED = "CLOSED",
    RECEIVED = "RECEIVED",
    CANCELLED = "CANCELLED", //new 
}

enum PreAuthStatus {
    DONE = "DONE",
    MANUAL = "MANUAL",
    PRE_AUTH = "PRE_AUTH", 
}

interface IShipmentSetStatus {
    shipment_num: string,
    status: ShipmentStatus
}



export const ShipmentsManage: React.FC = () => {

    const navigate = useNavigate(); // hook for navigation

    const selectDataQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/select/data"}`;
    const tableDataQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/table/data"}`;
    const queryExportReportUrl: string = `${process.env.REACT_APP_BASE_URL}/select/ares/shipment-line-view/xlsx`;

    const [shipmentNo, setShipmentNo] = useState<string>("");
    const [shipmentStatusStep, setShipmentStatusStep] = useState<string>("");
    const [sendShipment, setSendShipment] = useState<boolean>(false);
    const {fetchData, setRefreshTable}  = useAuth();
    const [stepNodes, setStepNodes] = useState<any[]>(initNodes);
    const [selectedShipmentNo, setSelectedShipmentNo] =  useState<string>("");

    // Processed Shipments End up here.
    const [shipmentElements, setShipmentElements] = useState<React.ReactElement[]>([]);
    // Processed Shipment Summary Lines end up here.
    const [shipmentSummaryElements, setShipmentSummaryElements] = useState<React.ReactElement[]>([]);

    const [currency, setCurrency] = useState<string>("");
    const [exchangeRate, setExchangeRate] = useState<string>("");
    const [isCurrencyValid, setIsCurrencyValid] = useState<boolean>(false);  
    const [isExchangeRateValid, setIsExchangeRateValid] = useState<boolean>(false); 
    const shipmentCurrencyExchangeQueryUrl = `${process.env.REACT_APP_BASE_URL}/functions/SHIPMENT-SET-CURRENCY-AND-EXCHANGE/run`;
    const shipmentProcessManualUrl = `${process.env.REACT_APP_BASE_URL}/functions/PROCESS-SHIPMENT-MANUAL/run`;

    const [landedCost, setLandedCost] = useState<string>("");
    const landedCostQueryUrl = `${process.env.REACT_APP_BASE_URL}/functions/PROCESS-SHIPMENT-PREAUTH/run`

    // Modal Show & Close
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showManualModal, setShowManualModal] = useState<boolean>(false);
    const [showPreAuthModal, setShowPreAuthModal] = useState<boolean>(false);
    const [showLandedCostModal, setShowLandedCostModal] = useState<boolean>(false);
    const [showCancelShipmentModal, setShowCancelShipmentModal] = useState<boolean>(false);
    


    //default span buttons design (disabled)
    const SpanReFetchBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Re-Fetch"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <RotateCcw style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }

    const SpanSendShipmentBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Send Shipment"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <Send style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }

    const SpanPreAuthBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Pre-Auth"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <FilePenLine style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }

    const SpanManualBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Manual"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <Pencil style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }

    const SpanLandedCostBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Landed Cost"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <PlaneLanding style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }
 
    const SpanCancelShipmentBtn = () => {
        return (
        <span className="btn h-100 d-flex align-items-center gap-1" title="Cancel Shipment"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <X style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }


    const SpanShipmentDetailedBtn = () => {
        return (
         <span className="btn h-100 d-flex align-items-center gap-1" title="Shipment Details"
              style={{ border: "1px solid #dee6ed", backgroundColor: "#e6e8eb", color: "#6c757d"}}
          >
          <BookOpenText style={{ width: "17px", height: "17px", color: "#6c757d" }} />
        </span>
        ) 
    }

    const progressBarTDStyle = {
        minWidth: '150px', 
        maxWidth: '160px',
        padding: '4px 6px'
    }


    const tdSmallerStyle  = {
        borderBottom: "1px solid #dee6ed",
        borderRight: "1px solid #dee6ed",
        padding: "8px",
        whiteSpace: "nowrap" as const,
    };


    const handleShipmentStatusChange = async (status: ShipmentStatus, reFetchShipmentNo?: string) => {

        const fnShipmentSetStatusUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SHIPMENT-SET-STATUS/run"}`;

        const shipmentSetStatus: IShipmentSetStatus = {
            shipment_num: shipmentNo.trim(),
            status: status
        } 

        if(reFetchShipmentNo) {
            shipmentSetStatus.shipment_num = reFetchShipmentNo.trim();
        }

        // console.log(`ShipmentNo: ${shipmentSetStatus.shipment_num}`);
      
        if(shipmentSetStatus.shipment_num.length === 0) {
            return;
        }
        
        setShipmentNo("");

       // fetchData(fnShipmentSetStatusUrl, {method:"POST", body: JSON.stringify(shipmentSetStatus)}, fetchShipments);
       fetchData(fnShipmentSetStatusUrl, 
        {method:"POST", body: JSON.stringify(shipmentSetStatus)},
        (result: any, status: string) =>  { 
            if(status === "200"){
                setRefreshTable(prev => !prev);
            }
        });  

    };

    // Shipment Summary -START
    const fetchShipmentSummary = async (shipmentNo: string, shipmentStatus: ShipmentStatus, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null, sendShipment:boolean = false) => {
        
        setShipmentNo(shipmentNo);
        setShipmentStatusStep(shipmentStatus);
        setSendShipment(sendShipment);

        setStepNodes((prevNodes) =>
            prevNodes.map((node) => {
                // Apply normal or currentStep styles to other nodes
                if (node.id === 'PREAUTH' && preAuthStatus !== null) {
                    return { ...node, style: preAuthStepInit }; 
                }
    
                return node.id === shipmentStatus
                    ? { ...node, style: currentStep }
                    : { ...node, style: normalStep };
            })
        );

        const shipmentLinesQuery: IQuerySelectData = {
            appCode: "ares",
            selectCode: "shipment-line-view",  
            where: 'shipment_num = :shipment_num',
            orderBy: 'item_code ASC',
            params: { 'shipment_num': `${shipmentNo}`}
        }; 

        fetchData(selectDataQueryUrl, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, (result: any) =>  processShipmentSummary(result, preAuthStatus, shipmentCurrency, shipmentExchangeRate));
        setShowModal(true);
    }

    // Process the Result into a table for display purposes.
    const processShipmentSummary = (result: any,  preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {


        const shipmentSummary: any[] = result.data;
        const shipmentTotalElements: React.ReactElement[] = [];
        const shipmentSummaryElements: React.ReactElement[] = [];
        const shipmentPreAuthorisedElements: React.ReactElement[] = [];

        if(preAuthStatus !== null){
           shipmentPreAuthorisedElements.push(
            <>
                <tr key='pre-authorised-header-sub'>
                    <td><b>Currency:</b></td>
                    <td><b>Exchange Rate:</b></td>
                </tr>
                <tr key='pre-authorised-body'>
                    <td className="fw-bold text-center">{shipmentCurrency}</td>
                    <td className="fw-bold text-center">{shipmentExchangeRate}</td>
                </tr> 
            </>)
        }
        
        let totalQuantity: number = 0;
        let totalReceived: number = 0;
        for(const key in shipmentSummary){
            totalQuantity += shipmentSummary[key].data?.qty_ordered;
            totalReceived = 0;
            
            const code = shipmentSummary[key].data?.item_code;
            const ordered = shipmentSummary[key].data?.qty_ordered;         

            shipmentSummaryElements.push(
                <tr key={key}>
                    <td className="text-center">{shipmentSummary[key].data?.item_code}</td>
                    <td className="text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                </tr>
            );
        }

        shipmentTotalElements.push(
            <>
                <tr key={shipmentSummary.length}>
                    <td><b>Total Unique Items:</b></td>
                    <td><b>Total Units Ordered:</b></td>
                </tr>            
                <tr key={shipmentSummary.length + 1} >
                    <td className="fw-bold text-center">{shipmentSummary.length}</td>
                    <td className="fw-bold text-center">{totalQuantity}</td>
                </tr>
                <tr>
                    <td><b>Item Code</b></td>
                    <td><b>Quantity</b></td>
                </tr>                
            </>
        );
    
        setShipmentSummaryElements([...shipmentPreAuthorisedElements, ...shipmentTotalElements, ...shipmentSummaryElements]);
    }

    const confirmShipmentSummary = async (confirmShipmentNo: string) => {
        setShowModal(false);
        handleShipmentStatusChange(ShipmentStatus.TO_SEND, confirmShipmentNo);
    }
    // Shipment Summary -END

    // Shipment Variance - START
    const fetchShipmentVariance = async (shipmentNo: string, shipmentStatus: ShipmentStatus, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {
        
        setShipmentNo(shipmentNo);
        setShipmentStatusStep(shipmentStatus);
        setSendShipment(false);

        setStepNodes((prevNodes) =>
            prevNodes.map((node) => {
                // Apply normal or currentStep styles to other nodes
                if (node.id === 'PREAUTH' && preAuthStatus !== null) {
                    return { ...node, style: preAuthStepInit }; 
                }
    
                return node.id === shipmentStatus
                    ? { ...node, style: currentStep }
                    : { ...node, style: normalStep };
            })
        );

        const shipmentLinesQuery: IQuerySelectData = {
            appCode: "ares",
            selectCode: "shipment-line-view",  
            where: 'shipment_num = :shipment_num',
            orderBy: 'item_code ASC',
            params: { 'shipment_num': `${shipmentNo}`}
        };  

        fetchData(selectDataQueryUrl, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, (result: any) =>  processShipmentVariance(result, preAuthStatus, shipmentCurrency, shipmentExchangeRate));
        setShowModal(true);
    }

    const processShipmentVariance = (result: any, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {

        const shipmentSummary: any[] = result.data;
        const shipmentTotalElements: React.ReactElement[] = [];
        const shipmentVairanceElements: React.ReactElement[] = [];
        const shipmentSummaryElements: React.ReactElement[] = [];
        const shipmentPreAuthorisedElements: React.ReactElement[] = [];

        if(preAuthStatus !== null){
            shipmentPreAuthorisedElements.push(
             <>
                 <tr key='pre-authorised-header-sub' style={{ width: "100%"}}>
                     <td><b>Pre-Auth Status:</b></td>
                     <td><b>Currency:</b></td>
                     <td><b>Exchange Rate:</b></td>
                 </tr>
                 <tr key='pre-authorised-body' style={{ width: "100%"}}>
                     <td className="fw-bold text-center">{preAuthStatus}</td>
                     <td className="fw-bold text-center">{shipmentCurrency}</td>
                     <td className="fw-bold text-center">{shipmentExchangeRate}</td>
                 </tr> 
             </>)
         }
        
    
        let totalQuantity: number = 0;
        let totalReceived: number = 0;
        for(const key in shipmentSummary){
            totalQuantity += shipmentSummary[key].data?.qty_ordered;
            totalReceived += shipmentSummary[key].data?.qty_received;
            
            const code = shipmentSummary[key].data?.item_code;
            const ordered = shipmentSummary[key].data?.qty_ordered;
            const received = shipmentSummary[key].data?.qty_received;
            
            if (ordered === received){ 
                shipmentSummaryElements.push(
                    <tr key={key}>
                        <td className="text-center">{shipmentSummary[key].data?.item_code}</td>
                        <td className="text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                        <td className="text-center">{shipmentSummary[key].data?.qty_received}</td>
                    </tr>
                );
            } else {
                shipmentVairanceElements.push(
                    <tr key={key}>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.item_code}</td>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.qty_received}</td>
                    </tr>
                );               
            }
        }

        shipmentTotalElements.push(
            <>
                <tr key={shipmentSummary.length}>
                    <td><b>Total Unique Items:</b></td>
                    <td><b>Total Units Ordered:</b></td>
                    <td><b>Total Units Received:</b></td>
                </tr>            
                <tr key={shipmentSummary.length + 1} >
                    <td className="fw-bold text-center">{shipmentSummary.length}</td>
                    <td className="fw-bold text-center">{totalQuantity}</td>
                    {totalQuantity !== totalReceived ? (
                            <td className="bg-danger text-white fw-bold text-center">{totalReceived}</td>
                        ) : (
                            <td className="bg-success text-dark fw-bold text-center">{totalReceived}</td>
                        )
                    }
                    
                </tr>
                <tr>
                    <td><b>Item Code</b></td>
                    <td><b>Quantity</b></td>
                    <td><b>Received</b></td>
                </tr>                
            </>
        );
    
        setShipmentSummaryElements([...shipmentPreAuthorisedElements, ...shipmentTotalElements, ...shipmentVairanceElements, ...shipmentSummaryElements]);
    }
    // Shipment Variance - END

    // Down load CSV - START
    // npm install xlsx-style file-saver
    // npm install @types/xlsx @types/file-saver --save-dev
    //
    const fetchShipmentVarianceExcel = async (confirmShipmentNo: string) => {
        
        setShipmentNo(confirmShipmentNo);
        setSendShipment(false);

        const template = "shipment_report.xlsx"; 
        const report = `${confirmShipmentNo}.xlsx`; 

        const urlWithParams = `${queryExportReportUrl}?templateFilename=apps/ares/${template}&reportFilename=${report}`;

        const shipmentLinesQuery: IQuerySelectData = {
            appCode: "ares",
            selectCode: "sa_m24l_shipment_line",  
            where: `shipment_num = :shipment_num`,
            orderBy: 'is_short DESC, item_code ASC',
            params: { 'shipment_num': `${confirmShipmentNo}` }
        }; 

        fetchData(urlWithParams, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, processShipmentVarianceExcel);
        setShowModal(true);
    }    

    const processShipmentVarianceExcel = async (result: any) => {

        setSendShipment(true);

        // Create a download link for the Blob
        const url = URL.createObjectURL(result);

        // Create a temporary <a> element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        //a.download = `${confirmShipmentNo}.xlsx`; // Set the default filename here
        a.download = `${shipmentNo}.xlsx`; // Set the default filename here
        document.body.appendChild(a);
        a.click();

        
    }    
    // Down load CSV - END    

     //hanlding pmanual authorization modal
    const handleManualModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowManualModal(true);
    }

    //hanlding pre-authorization modal
    const handlePreAuthModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowPreAuthModal(true);
    }

    //handling cost landed modal 
    const handleLandedCostModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowLandedCostModal(true);
    }

    const validateExchangeRate = (value: string) => {
        //[0, 00, 000, 0.0, 0.00, 0.000, 00.0, 00.00, 00.000, 000.0, 000.00, 000.000, abc]	-> invalid
        const regex = /^(?!0+(?:\.0+)?$)\d+(?:\.\d{1,4})?$/;
        return regex.test(value);
    };

    const handleOnChange = (e: any) => {
        const { value, name } = e.target;
    
        if (name === "currency") {
            setCurrency(value); 
            setIsCurrencyValid(value !== "");
        }
    
        if (name === "exchange_rate") {
            setExchangeRate(value); 
            const isValidExchangeRate = validateExchangeRate(value);
            setIsExchangeRateValid(isValidExchangeRate); 
        }

        if(name === "landed_cost"){
           setLandedCost(value);
        }
        
      };


    const handlePreAuthSubmit = async () => { 

        if(currency !== "" && exchangeRate !== ""){
            fetchData(shipmentCurrencyExchangeQueryUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, "currency": currency, "exchange_rate": exchangeRate })},
                (result: any, status: string) =>  { 
                    if(status === "200"){
                      setRefreshTable(prev => !prev);
                    }
                });  
        }else{
            return;
        }

        //ressetting state and closing modal
        resetPreAuthStates();
    }

    const handleManualSubmit = async () => { 
        if(currency !== "" && exchangeRate !== ""){
            fetchData(shipmentProcessManualUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, "currency": currency, "exchange_rate": exchangeRate })},
                (result: any, status: string) =>  { 
                    if(status === "200"){
                      setRefreshTable(prev => !prev);
                    }
                });  
        }else{
            return;
        }

        //ressetting state and closing modal
        resetPreAuthStates();
    }

    const handleLandedCostSubmit = async () => {
        if(landedCost !== ""){
            fetchData(landedCostQueryUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, "landed_cost_doc_num": landedCost })},
                (result: any, status: string) =>  { 
                    if(status === "200"){
                      setRefreshTable(prev => !prev);
                    }
                });  
        }else{
            return;
        }
        //ressetting state and closing modal
        resetLandedCostsState();
    }

    // handle cancel of shipment 
    const handleCancelShimpmentModal = (shipmentNo: string) => {
        console.log(`we got into here`);
        setShowCancelShipmentModal(true);
        setSelectedShipmentNo(shipmentNo);
    }


    // This function re-fetches shipment data for display. 
    // const refreshTable = () => {
    //     fetchShipments();
    // }

    const handleCancelShipmentSubmit = () => {
        if(selectedShipmentNo !== ""){
            const fnShipmentSetStatusUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SHIPMENT-SET-STATUS/run"}`;
            fetchData(fnShipmentSetStatusUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": selectedShipmentNo.trim() , "status": ShipmentStatus.CANCELLED })},
                (result: any, status: string) =>  { 
                    if(status === "200"){
                      setRefreshTable(prev => !prev);
                    }
                });  
        }else{
            return;
        }
        //reset fields
        setSelectedShipmentNo(""); 
        setShowCancelShipmentModal(false);
    }


    const resetPreAuthStates = () => {
        setCurrency("");
        setExchangeRate("");
        setShowPreAuthModal(false);  
        setShowManualModal(false);  
        setShipmentNo("");   
        setIsCurrencyValid(false);
        setIsExchangeRateValid(false);
    }

    //resetting landed costs states
    const resetLandedCostsState = () => {
        setShipmentNo("");  
        setLandedCost("");
        setShowLandedCostModal(false);
    }

    const thStyle = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };

  
    const getStatusColor = (status: string): { backgroundColor: string; color: string; } | {} => {
        if (IntegrationStatus[status as keyof typeof IntegrationStatus]) {
            return { backgroundColor: IntegrationStatus[status as keyof typeof IntegrationStatus], color: 'white' };
        }
        return {};
    };

  
    return(
        <React.Fragment>
            <Container fluid className="p-0">
                <QueryListProvider>
                    <QueryTableList
                        listTitle={"Fetch, Confirm, Send & Receive Shipments"}
                        recordTitle={"Fetch, Confirm, Send & Receive Shipments"}
                        tableName={"sa_m24l_shipment_management"}
                        editTableName={"sa_m24l_shipment_management"}
                        hasIntegrationStatusLegend={false} 
                        errorCategoryName={''}
                        editHasCompoundKey={false}   
                        showFailedTransactionsTable={false}
                        query={{
                            appCode: "ares",
                            selectCode: "shipment-notification-projection",
                            tableName: null,
                            where: "",
                            orderBy: '',
                            pageNum: 0,
                            pageSize: 100,
                            params: {},
                        }} 
                        orderColumns={[
                            { name: "shipment_num", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "landed_cost_doc_num", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "progress", type: "", sortable: false, searchable: false },
                            { name: "pre_auth_status", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "integration_status", type: "VARCHAR", sortable: true, searchable: true },
                            { name: "created", type: "TIMESTAMP", sortable: true, searchable: false },
                            { name: "updated", type: "TIMESTAMP", sortable: true, searchable: false }
                        ]}
                        reportOptions={{ 
                                reportQuery: {
                                appCode: "ares",
                                selectCode: "shipment-notification-projection",
                                tableName: null, 
                                where: "",
                                orderBy: "",
                                pageNum: 0,
                                pageSize: 0,
                                params: {},
                            },
                            templateName: "apps/ares/shipment_summary_report.xlsx", 
                            reportName: "shipment_summary_report.xlsx",
                            reportData: null
                        }}   
                        renderRowData={(row) => {


                            const statusColor = getStatusColor(row.integration_status);
                            const tdStyle: React.CSSProperties = { whiteSpace: 'nowrap', border: '1px solid #deebed',  padding: '4px 8px', fontSize: '0.75rem' };
                            const progressBarTDStyle: React.CSSProperties = {...tdStyle, minWidth: "200px"};

                            // Helper to get progress percentage based on shipment status
                            const getProgress = (status: string) => {
                                switch (status) {
                                case ShipmentStatus.TO_FETCH:
                                    return 1;
                                case ShipmentStatus.FETCHED:
                                    return 5;
                                case ShipmentStatus.TO_SEND:
                                    return 10;
                                case ShipmentStatus.SENT:
                                    return 15;
                                case ShipmentStatus.SUCCESS:
                                    return 20;
                                case ShipmentStatus.ERROR:
                                    return 0;
                                case ShipmentStatus.ARRIVED:
                                    return 50;
                                case ShipmentStatus.CLOSED:
                                    return 100;
                                case ShipmentStatus.RECEIVED:
                                    return 100;
                                case ShipmentStatus.CANCELLED:
                                    return 100;
                                default:
                                    return 0;
                                }
                            };

                            const progress = getProgress(row.integration_status);

                            return (
                                <>
                                    <td style={{...tdStyle, position: "relative"}}>
                                        
                                        {row.shipment_num === row.reference && row.repeat_count > 0 && (
                                            <p className="text-center"
                                                style={{position: "absolute", top: 0, right: 0, marginRight: "2px", fontSize: "10px", fontWeight: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: "pointer" }}
                                                onClick={() => navigate("/m24l/notifications-new", { state: { shipment_num: row.shipment_num , category: "" }})}
                                                title="This shipment has an error notification"
                                            >
                                            <b style={{ color: "white" }}>{row.repeat_count}</b>
                                            </p>
                                        )}
                                        {row.shipment_num}
                                        
                                    </td>
                                    <td style={tdStyle}>{row.landed_cost_doc_num}</td>
                                    <td style={progressBarTDStyle}>
                                        <div className="progress w-100" style={{ width: "fit-content" }}>
                                            <div className="progress-bar bg-success" role="progressbar" style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
                                                {progress}%
                                            </div>
                                        </div>
                                    </td>
                                    <td style={tdStyle}>
                                        {row.pre_auth_status !== null && 'Pre-Auth'}
                                    </td>
                                    <td style={{...tdStyle,...statusColor, textAlign: "center", verticalAlign: "middle"}}>
                                        {row.integration_status}
                                    </td>
                                    <td style={tdStyle}>{row.created}</td>
                                    <td style={tdStyle}>{row.updated}</td>
                                </>
                            );
                        }}
                        renderActions={(row) => (
                            <td className="sa-record-control" style={{ border: '1px solid #deebed', height: '45px', fontSize: '0.55rem', padding: '0 5px'}}>
                            <div className="d-flex gap-2">
                                { /* row.integration_status === ShipmentStatus.TO_FETCH */ }
                                { row.integration_status === ShipmentStatus.TO_FETCH && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>            
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <SpanShipmentDetailedBtn/> 
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Cancel Shipment"
                                        onClick={() => handleCancelShimpmentModal(row.shipment_num)}
                                    >
                                        <X style={{ width: "17px", height: "17px" }} />
                                    </button> 
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.FETCHED */ }
                                { row.integration_status === ShipmentStatus.FETCHED && 
                                    <>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Re-Fetch"
                                        onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH, row.shipment_num)}
                                        >
                                        <RotateCcw style={{ width: "17px", height: "17px" }}  /> 
                                    </button>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Send Shipment"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate, true)}
                                        >
                                        <Send style={{ width: "17px", height: "17px" }}  /> 
                                    </button>
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Cancel Shipment"
                                        onClick={() => handleCancelShimpmentModal(row.shipment_num)}
                                    >
                                        <X style={{ width: "17px", height: "17px" }} />
                                    </button>  
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.TO_SEND */ }
                                { row.integration_status === ShipmentStatus.TO_SEND && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Cancel Shipment"
                                        onClick={() => handleCancelShimpmentModal(row.shipment_num)}
                                    >
                                        <X style={{ width: "17px", height: "17px" }} />
                                    </button>    
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.SEND */ }
                                { row.integration_status === ShipmentStatus.SENT && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px", }}/>
                                    </button>   
                                    <SpanCancelShipmentBtn/>
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.SUCCESS */ }
                                { row.integration_status === ShipmentStatus.SUCCESS && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>    
                                    { 
                                    row.pre_auth_status === null ? 
                                        <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Pre-Auth"
                                            onClick={() => handlePreAuthModal(row.shipment_num)}
                                        > 
                                            <FilePenLine  style={{ width: "17px", height: "17px" }} />
                                        </button>  
                                    : 
                                        <SpanPreAuthBtn/>
                                    }          
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }} />
                                    </button>   
                                    <SpanCancelShipmentBtn/>   
                                    </>
                                }
                            
                                { /* row.integration_status === ShipmentStatus.ERROR */ }
                                { row.integration_status === ShipmentStatus.ERROR && 
                                    <>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Re-Fetch"
                                        onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH, row.shipment_num)}
                                    >
                                        <RotateCcw style={{ width: "17px", height: "17px" }}/>
                                    </button>  
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Send Shipment"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate, true)}
                                    >
                                    <Send style={{ width: "17px", height: "17px", }}/>
                                    </button>           
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px",  }}/>
                                    </button>   
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Cancel Shipment"
                                        onClick={() => handleCancelShimpmentModal(row.shipment_num)}
                                    >
                                        <X style={{ width: "17px", height: "17px" }} />
                                    </button> 
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.ARRIVED */ }
                                { row.integration_status === ShipmentStatus.ARRIVED && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    { row.pre_auth_status === null ? 
                                        <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Pre-Auth"
                                            onClick={() => handlePreAuthModal(row.shipment_num)}
                                        > 
                                            <FilePenLine  style={{ width: "17px", height: "17px" }} />
                                        </button>  
                                    : 
                                        <SpanPreAuthBtn/>
                                    } 
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentSummary(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <SpanCancelShipmentBtn/>
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.CLOSED */ }
                                { row.integration_status === ShipmentStatus.CLOSED && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentVariance(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <SpanCancelShipmentBtn/>
                                    </> 
                                }
                                { /* row.integration_status === ShipmentStatus.RECEIVED */ }
                                { row.integration_status === ShipmentStatus.RECEIVED && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    <SpanPreAuthBtn/>
                                    {row.pre_auth_status === null ? (
                                    <>
                                        <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Manual"
                                            onClick={() => handleManualModal(row.shipment_num)}
                                        >
                                        <Pencil style={{ width: "17px", height: "17px" }} />
                                        </button>   
                                        <SpanLandedCostBtn/>
                                    </>
                                    ) : (
                                        <>
                                        <SpanManualBtn/>
                                        { row.landed_cost_doc_num ? 
                                            (
                                                <SpanLandedCostBtn />
                                            )
                                            : 
                                            (
                                            <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Landed Cost"
                                                onClick={() => handleLandedCostModal(row.shipment_num)}
                                            >
                                                <PlaneLanding style={{ width: "17px", height: "17px" }} />
                                            </button>
                                            )
                                            }
                                        </>
                                    )}   
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentVariance(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <SpanCancelShipmentBtn/>
                                    </>
                                }
                                { /* row.integration_status === ShipmentStatus.CANCELLED */ }
                                { row.integration_status === ShipmentStatus.CANCELLED && 
                                    <>
                                    <SpanReFetchBtn/>   
                                    <SpanSendShipmentBtn/>               
                                    <SpanPreAuthBtn/>
                                    <SpanManualBtn/>
                                    <SpanLandedCostBtn/>
                                    <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" title="Shipment Details"
                                        onClick={() => fetchShipmentVariance(row.shipment_num, row.integration_status, row.pre_auth_status, row.currency, row.exchange_rate)}
                                    >
                                    <BookOpenText style={{ width: "17px", height: "17px" }}/>
                                    </button>   
                                    <SpanCancelShipmentBtn/>
                                    </>
                                }
                            </div>
                            </td>
                        )}     

                        headerControls={
                        <>
                            <label className="form-check-label mt-0 mb-1" htmlFor="shipment"><b>Shipment No:</b></label>
                            <input id="shipment" type="text" value={shipmentNo} onChange={(e) => setShipmentNo(e.target.value)} placeholder="Shipment No." className="form-control form-control-md mb-3 w-25" autoComplete="off"/>
                            <button className="btn btn-secondary form-control form-control-md mb-3 w-25" onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH)}>
                                Fetch Shipment
                            </button>
                        </>
                        }                
                    />
                </QueryListProvider>
          

            {
                // [Modals Sections]
                // = Shipment Process 
                // = Pre-Authorization
                // = Manual
                // = Landed Cost
                // = Cancel Shipment
            }

             {/* Modal structure [start] */}
             <Modal show={showModal} onHide={() => { setShowModal(false); setShipmentNo(""); }}>
                <Modal.Header closeButton>
                <Modal.Title>
                    {shipmentNo}
                </Modal.Title>
                <div>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <Button variant="secondary" onClick={()=>fetchShipmentVarianceExcel(shipmentNo)}>
                        Download
                    </Button>
                </div>                            
                </Modal.Header>
                <Modal.Body>
                    <p><b>Shipment Process:</b></p>
                    <div className="react-flow-wrapper" style={{ height: '120px', width: '700px' }}>
                        <ReactFlow 
                            nodes={stepNodes}
                            edges={stepEdges}
                            zoomOnScroll={false}  // Disable zooming with mouse scroll
                            zoomOnPinch={false}   // Disable zooming with pinch gestures
                            zoomOnDoubleClick={false}  // Disable zooming with double click
                            panOnDrag={false}  // Disable panning by dragging the canvas
                            minZoom={1}        // Lock the zoom to a fixed level
                            maxZoom={1}        // Prevent zooming in or out                                                                       
                        >
                        <Controls
                            showInteractive={false}
                            showFitView={false}
                            showZoom={false}
                            orientation="horizontal"
                        />
                        </ReactFlow>
                    </div>                                          
                    <table className="table table-bordered">
                        <thead></thead>
                        <tbody>
                            {shipmentSummaryElements}
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer>
                <Button variant="secondary" onClick={() => { setShowModal(false); setShipmentNo("")}}>
                    Close
                </Button>
                {sendShipment &&
                    <Button variant="danger" onClick={()=>confirmShipmentSummary(shipmentNo)}>
                        Confirm & Send
                    </Button>
                }
                </Modal.Footer>
            </Modal>
             {/* Modal structure [end] */}


            { /* Pre-Authorization Modal [start] */ }
            <Modal show={showPreAuthModal} onHide={() =>  {resetPreAuthStates();}}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {shipmentNo}
                    </Modal.Title>                         
                </Modal.Header>
                <Modal.Body>
                    <p><b>Pre-Auth Process:</b></p>                            
                    <table className="table table-bordered">
                        <tbody style={{ display: 'flex', gap: '10px' }}>
                            <select className="form-control form-control-md" 
                                id="currency" 
                                name="currency" 
                                value={currency} 
                                onChange={handleOnChange} 
                                style={{ width: '220px', borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da'}}>
                                <option value="">--Select Currency Option--</option>
                                <option value="AUD">AUD</option>
                                <option value="BWP">BWP</option>
                                <option value="ETB">ETB</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="GHS">GHS</option>
                                <option value="JPY">JPY</option>
                                <option value="KES">KES</option>
                                <option value="MUR">MUR</option>
                                <option value="MWK">MWK</option>
                                <option value="MZN">MZN</option>
                                <option value="NGN">NGN</option>
                                <option value="NZD">NZD</option>
                                <option value="R">R</option>
                                <option value="RWF">RWF</option>
                                <option value="SGD">SGD</option>
                                <option value="TZS">TZS</option>
                                <option value="USD">USD</option>
                                <option value="ZAR">ZAR</option>
                                <option value="ZMW">ZMW</option>
                            </select>
                            <input 
                            id="exchange_rate"
                            value={exchangeRate}
                            name="exchange_rate"
                            onChange={handleOnChange}
                            type="text" 
                            autoComplete="off" 
                            placeholder={`Enter Exchange Rate... Format - eg: 00.0000`} 
                            className="form-control form-control-md"
                            style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                            </input>
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button variant="primary" disabled={!isCurrencyValid || !isExchangeRateValid} onClick={() => handlePreAuthSubmit()}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => {resetPreAuthStates();}}>
                    Close
                </Button>
                </Modal.Footer>
            </Modal>
            { /* Pre-Authorization Modal [end] */ }


            { /* Manual Modal [start] */ }
            <Modal show={showManualModal} onHide={() =>  { resetPreAuthStates();}}>
                <Modal.Header closeButton>
                <Modal.Title>
                    {shipmentNo}
                </Modal.Title>                         
                </Modal.Header>
                <Modal.Body>
                    <p><b>Manual Process:</b></p>                               
                    <table className="table table-bordered">
                        <tbody style={{ display: 'flex', gap: '10px' }}>
                            <select className="form-control form-control-md" 
                                id="currency" 
                                name="currency" 
                                value={currency} 
                                onChange={handleOnChange} 
                                style={{ width: '220px', borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da'}}>
                                <option value="">--Select Currency Option--</option>
                                <option value="AUD">AUD</option>
                                <option value="BWP">BWP</option>
                                <option value="ETB">ETB</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="GHS">GHS</option>
                                <option value="JPY">JPY</option>
                                <option value="KES">KES</option>
                                <option value="MUR">MUR</option>
                                <option value="MWK">MWK</option>
                                <option value="MZN">MZN</option>
                                <option value="NGN">NGN</option>
                                <option value="NZD">NZD</option>
                                <option value="R">R</option>
                                <option value="RWF">RWF</option>
                                <option value="SGD">SGD</option>
                                <option value="TZS">TZS</option>
                                <option value="USD">USD</option>
                                <option value="ZAR">ZAR</option>
                                <option value="ZMW">ZMW</option>
                            </select>
                            <input 
                            id="exchange_rate"
                            value={exchangeRate}
                            name="exchange_rate"
                            onChange={handleOnChange}
                            type="text" 
                            autoComplete="off" 
                            placeholder={`Enter Exchange Rate... Format - eg: 00.0000`} 
                            className="form-control form-control-md"
                            style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                            </input>
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button variant="primary" disabled={!isCurrencyValid || !isExchangeRateValid} onClick={() => handleManualSubmit()}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { resetPreAuthStates();}}>
                    Close
                </Button>
                </Modal.Footer>
            </Modal>
            { /* Manual Modal [end] */ }


            { /* Landed Cost Modal [start] */ }
            <Modal show={showLandedCostModal} onHide={() =>  { resetLandedCostsState();}}>
                <Modal.Header closeButton>
                <Modal.Title>
                    {shipmentNo}
                </Modal.Title>                         
                </Modal.Header>
                <Modal.Body>
                    <p><b>Landed Cost Process:</b></p>                            
                    <table className="table table-bordered">
                        <tbody style={{ display: 'flex', gap: '10px' }}>
                            <input 
                            id="landed_cost"
                            value={landedCost}
                            name="landed_cost"
                            onChange={handleOnChange}
                            type="text" 
                            autoComplete="off" 
                            placeholder={`Enter Landed Cost Doc Num.....`} 
                            className="form-control form-control-md"
                            style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                            </input>
                        </tbody>
                    </table>
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button variant="primary" disabled={landedCost === "" ? true : false} onClick={() => handleLandedCostSubmit()}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { resetLandedCostsState();}}>
                    Close
                </Button>
                </Modal.Footer>
            </Modal>
            { /* Landed Cost Modal [end] */ }

            { /* Cancel Shipment Modal [start] */ }
            <Modal show={showCancelShipmentModal} onHide={() =>  {setShowCancelShipmentModal(false); setSelectedShipmentNo(""); }}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedShipmentNo}
                    </Modal.Title>                         
                </Modal.Header>
                <Modal.Body>
                    <p><b>Are you sure you want to cancel this shipment? This action cannot be undone.</b></p>
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Button variant="primary" onClick={() => handleCancelShipmentSubmit()}>
                    Confirm
                </Button>
                <Button variant="secondary" onClick={() => { setShowCancelShipmentModal(false); setSelectedShipmentNo(""); }} >
                    Close
                </Button>
                </Modal.Footer>
            </Modal>
            { /* Cancel Shipment Modal [end] */ }


            </Container>
        </React.Fragment>
    );
}

